/**
 * Working calendars (decisions-log V305, V306). A project follows «Будние дни» (Mon–Fri, as before),
 * the Belarusian, the Georgian or the Uzbek calendar: its workdays are Mon–Fri less the holidays of
 * that year plus the weekend days declared working (the Belarusian and Uzbek transfers). The days
 * of a year come from a public source or by hand (Settings → «Календари»); the plain calendar has
 * no days at all.
 */

export const WORK_CALENDARS = ['weekdays', 'BY', 'GE', 'UZ'] as const;
export type WorkCalendarCode = (typeof WORK_CALENDARS)[number];

/** Countries whose days are kept; a project on one of them follows its calendar. */
export const CALENDAR_COUNTRIES = ['BY', 'GE', 'UZ'] as const;
export type CalendarCountry = (typeof CALENDAR_COUNTRIES)[number];

export const isCalendarCountry = (value: string): value is CalendarCountry =>
  (CALENDAR_COUNTRIES as readonly string[]).includes(value);

/** `holiday`: a weekday off; `workday`: a Saturday or Sunday worked instead (a transfer). */
export const CALENDAR_DAY_KINDS = ['holiday', 'workday'] as const;
export type CalendarDayKind = (typeof CALENDAR_DAY_KINDS)[number];

export const WORK_CALENDAR_LABELS: Readonly<Record<WorkCalendarCode, string>> = {
  weekdays: 'Будние дни',
  BY: 'Беларусь',
  GE: 'Грузия',
  UZ: 'Узбекистан',
};

export const CALENDAR_COUNTRY_LABELS: Readonly<Record<CalendarCountry, string>> = { BY: 'Беларусь', GE: 'Грузия', UZ: 'Узбекистан' };

/** «календарь Беларуси» / «календарь Грузии». */
export const CALENDAR_COUNTRY_GENITIVE: Readonly<Record<CalendarCountry, string>> = { BY: 'Беларуси', GE: 'Грузии', UZ: 'Узбекистана' };

export interface CalendarDay {
  readonly date: string;
  readonly kind: CalendarDayKind;
}

export interface WorkCalendar {
  /** Weekdays that are not worked. */
  readonly holidays: ReadonlySet<string>;
  /** Saturdays and Sundays that are worked. */
  readonly workingWeekends: ReadonlySet<string>;
}

/** Mon–Fri with no holidays: what every project followed before V305. */
export const WEEKDAYS_CALENDAR: WorkCalendar = { holidays: new Set(), workingWeekends: new Set() };

export function workCalendarOf(days: readonly CalendarDay[]): WorkCalendar {
  return {
    holidays: new Set(days.filter((day) => day.kind === 'holiday').map((day) => day.date)),
    workingWeekends: new Set(days.filter((day) => day.kind === 'workday').map((day) => day.date)),
  };
}
